import { useCallback, useMemo } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Conversation, SlackMessage, SlackUser } from '@assembly/slack'
import { usePlatform } from '../platform'
import { useWorkspace } from './workspace'
import { keys } from './keys'
import type { HistoryPage } from './cache'

const MINUTE = 60_000

// ------------------------------------------------------------------ users

export function useUsers() {
  const { account, client } = useWorkspace()
  const qc = useQueryClient()
  return useQuery({
    queryKey: keys.users(account.id),
    queryFn: async () => {
      const fresh = new Map((await client.users.list()).map((u) => [u.id, u] as const))
      // users.list only has your own workspace; keep people from other organizations
      // that useResolveUsers() looked up individually.
      for (const [id, u] of qc.getQueryData<Map<string, SlackUser>>(keys.users(account.id)) ?? []) if (!fresh.has(id)) fresh.set(id, u)
      return fresh
    },
    staleTime: 30 * MINUTE,
  })
}

/** True for people from another organization (Slack Connect). */
export function isExternalUser(user: SlackUser | undefined, teamId: string): boolean {
  if (!user?.team_id || user.is_bot) return false
  if (user.team_id === teamId) return false
  return !user.enterprise_user?.teams?.includes(teamId)
}

/** Another organization's name and icon (team.info works for orgs you share channels with). */
export function useOrg(teamId: string | undefined) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.org(account.id, teamId ?? ''),
    queryFn: () => client.team.info(teamId),
    enabled: !!teamId,
    staleTime: 24 * 60 * MINUTE,
    retry: false,
  })
}

/** The organization a person belongs to, if it isn't yours. */
export function useExternalOrg(user: SlackUser | undefined) {
  const { account } = useWorkspace()
  const external = isExternalUser(user, account.teamId)
  return useOrg(external ? user?.team_id : undefined).data ?? null
}

/** A user from the directory, falling back to users.info for externals and bots. */
export function useUser(id: string | undefined): SlackUser | undefined {
  const { account, client } = useWorkspace()
  const { data: users, isSuccess } = useUsers()
  const known = id ? users?.get(id) : undefined
  const { data } = useQuery({
    queryKey: keys.user(account.id, id ?? ''),
    queryFn: () => client.users.info(id!),
    enabled: !!id && isSuccess && !known && /^[UW]/.test(id),
    staleTime: 60 * MINUTE,
    retry: false,
  })
  return known ?? data
}

export function displayName(user: SlackUser | undefined, fallback = 'Unknown'): string {
  if (!user) return fallback
  return user.profile?.display_name || user.profile?.real_name || user.real_name || user.name || fallback
}

export function fullName(user: SlackUser | undefined): string {
  if (!user) return ''
  return user.profile?.real_name || user.real_name || user.name
}

export function useDisplayName() {
  const { data: users } = useUsers()
  return useCallback((id: string) => displayName(users?.get(id), id), [users])
}

export function useTeam() {
  const { account, client } = useWorkspace()
  return useQuery({ queryKey: keys.team(account.id), queryFn: () => client.team.info(), staleTime: 60 * MINUTE })
}

export function usePresence(user: string | undefined) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.presence(account.id, user ?? ''),
    queryFn: () => client.users.getPresence(user!).then((r) => r.presence),
    enabled: !!user,
    staleTime: 2 * MINUTE,
    refetchInterval: 5 * MINUTE,
  })
}

// ------------------------------------------------------------------ conversations

export function useConversations() {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.conversations(account.id),
    queryFn: () => client.users.conversations(),
    staleTime: 5 * MINUTE,
    refetchInterval: 5 * MINUTE,
  })
}

export function useConversation(id: string | null | undefined) {
  const { account, client } = useWorkspace()
  const { data: list } = useConversations()
  return useQuery({
    queryKey: keys.conversation(account.id, id ?? ''),
    queryFn: () => client.conversations.info(id!),
    enabled: !!id,
    placeholderData: () => list?.find((c) => c.id === id),
    staleTime: 2 * MINUTE,
  })
}

/** Participant handles from an mpim name like "mpdm-alice--bob--carol-1". */
function mpimHandles(name: string): string[] {
  return name.replace(/^mpdm-/, '').replace(/-\d+$/, '').split('--')
}

export function useConversationName() {
  const { data: users } = useUsers()
  const me = useWorkspace().account.userId
  return useCallback(
    (c: Conversation | undefined): string => {
      if (!c) return ''
      if (c.is_im) return c.user === me ? `${displayName(users?.get(c.user!))} (you)` : displayName(users?.get(c.user ?? ''), c.user)
      if (c.is_mpim && c.name) {
        const byHandle = new Map([...(users?.values() ?? [])].map((u) => [u.name, u]))
        return mpimHandles(c.name)
          .filter((h) => byHandle.get(h)?.id !== me)
          .map((h) => displayName(byHandle.get(h), h))
          .join(', ')
      }
      return c.name ?? c.id
    },
    [users, me],
  )
}

export function usePublicChannels(includeArchived = false) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.publicChannels(account.id, includeArchived),
    queryFn: () => client.conversations.list('public_channel', !includeArchived),
    staleTime: 5 * MINUTE,
  })
}

export function useMembers(channel: string | undefined) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.members(account.id, channel ?? ''),
    queryFn: () => client.conversations.members(channel!),
    enabled: !!channel,
    staleTime: 5 * MINUTE,
  })
}

// ------------------------------------------------------------------ messages

export function useHistory(channel: string | null) {
  const { account, client } = useWorkspace()
  return useInfiniteQuery({
    queryKey: keys.history(account.id, channel ?? ''),
    queryFn: async ({ pageParam }): Promise<HistoryPage> => {
      const res = await client.conversations.history(channel!, { cursor: pageParam, limit: 50 })
      return { messages: res.messages, cursor: res.has_more ? res.response_metadata?.next_cursor : undefined }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor || undefined,
    enabled: !!channel,
    staleTime: Infinity, // kept fresh by realtime events / polling
  })
}

export function useReplies(channel: string, ts: string) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.replies(account.id, channel, ts),
    queryFn: async () => {
      const all: SlackMessage[] = []
      let cursor: string | undefined
      do {
        const res = await client.conversations.replies(channel, ts, { cursor })
        all.push(...res.messages)
        cursor = res.has_more ? res.response_metadata?.next_cursor : undefined
      } while (cursor)
      return all
    },
    staleTime: Infinity,
  })
}

export function usePins(channel: string | undefined) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.pins(account.id, channel ?? ''),
    queryFn: () => client.pins.list(channel!),
    enabled: !!channel,
  })
}

export function useBookmarks(channel: string | undefined) {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.bookmarks(account.id, channel ?? ''),
    queryFn: () => client.bookmarks.list(channel!),
    enabled: !!channel,
    retry: false,
    staleTime: 5 * MINUTE,
  })
}

// ------------------------------------------------------------------ misc

export function useCustomEmoji() {
  const { account, client } = useWorkspace()
  return useQuery({ queryKey: keys.emoji(account.id), queryFn: () => client.emoji.list(), staleTime: 60 * MINUTE })
}

export function useUserGroups() {
  const { account, client } = useWorkspace()
  return useQuery({
    queryKey: keys.usergroups(account.id),
    queryFn: () => client.usergroups.list(),
    staleTime: 60 * MINUTE,
    retry: false,
  })
}

export function useDnd() {
  const { account, client } = useWorkspace()
  return useQuery({ queryKey: keys.dnd(account.id), queryFn: () => client.dnd.info(), staleTime: MINUTE, refetchInterval: 5 * MINUTE })
}

export function useReminders() {
  const { account, client } = useWorkspace()
  return useQuery({ queryKey: keys.reminders(account.id), queryFn: () => client.reminders.list() })
}

export function useScheduledMessages() {
  const { account, client } = useWorkspace()
  return useQuery({ queryKey: keys.scheduled(account.id), queryFn: () => client.chat.scheduledMessages() })
}

/** Loads a private Slack file as an object URL using the platform's authenticated fetch. */
export function useFileUrl(url: string | undefined) {
  const { account } = useWorkspace()
  const platform = usePlatform()
  return useQuery({
    queryKey: keys.file(account.id, url ?? ''),
    queryFn: async () => URL.createObjectURL(await platform.fetchFile(url!, account.token)),
    enabled: !!url,
    staleTime: Infinity,
    gcTime: 30 * MINUTE,
  })
}

export function useQueryClientScoped() {
  const qc = useQueryClient()
  const { account } = useWorkspace()
  return useMemo(() => ({ qc, accountId: account.id }), [qc, account.id])
}
